import { Component } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  Data,
  NavigationEnd,
  ParamMap,
  Router,
  RouterModule,
} from '@angular/router';
import { TablerIconsModule } from 'angular-tabler-icons';
import { filter, map, mergeMap, startWith } from 'rxjs/operators';
import { PageInfoFactory } from './page-info.factory';
import { DemandService } from 'src/app/services/apps/demand/demand.service';
import { ServiceOrderService } from 'src/app/services/apps/service-order/service-order.service';
import { VisitService } from 'src/app/services/apps/visit/visit.service';
import { BudgetProviderService } from 'src/app/services/apps/budget/budget-provider.service';
import { BudgetTakerService } from 'src/app/services/apps/budget/budget-taker.service';
import { SessionPermissionService } from 'src/app/services/apps/auth/session-permission.service';

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [RouterModule, TablerIconsModule],
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
})
export class AppBreadcrumbComponent {
  pageInfo: Data | any = Object.create(null);
  isLoading = true;
  private routeBuildVersion = 0;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private demandService: DemandService,
    private serviceOrderService: ServiceOrderService,
    private visitService: VisitService,
    private budgetProviderService: BudgetProviderService,
    private budgetTakerService: BudgetTakerService,
    private sessionPermissions: SessionPermissionService,
  ) {
    this.initRouteListener();
  }

  private initRouteListener() {
    this.router.events
      .pipe(
        startWith(null),
        filter((event) => event === null || event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) =>
          route.data.pipe(
            map((data) => ({
              data,
              params: route.snapshot.paramMap,
              queryParams: route.snapshot.queryParamMap,
            })),
          ),
        ),
      )
      .subscribe(async ({ data, params, queryParams }) => {
        const currentBuildVersion = ++this.routeBuildVersion;
        this.isLoading = true;
        this.pageInfo = Object.create(null);
        this.titleService.setTitle(data['title'] + ' - proFinders');

        const pageInfo = await this.buildPageInfo(
          this.router.url,
          data,
          params,
          queryParams,
        );

        if (currentBuildVersion !== this.routeBuildVersion) {
          return;
        }

        this.titleService.setTitle(pageInfo.title + ' - proFinders');
        this.pageInfo = { ...data, ...pageInfo };
        this.isLoading = false;
      });
  }

  private async buildPageInfo(
    route: string,
    data: any,
    params: ParamMap,
    queryParams: ParamMap,
  ) {
    const factory = new PageInfoFactory(
      this.tenantType,
      this.tenantType === 'Tomador' &&
        this.sessionPermissions.getProfileName() === 'Aprovador',
      {
        demandService: this.demandService,
        serviceOrderService: this.serviceOrderService,
        budgetTakerService: this.budgetTakerService,
        budgetProviderService: this.budgetProviderService,
        visitService: this.visitService,
      },
    );

    const pageInfo = await factory.build(route, params, queryParams);
    pageInfo.pagesAbove = this.replaceRouteParams(pageInfo.pagesAbove, params);
    return pageInfo;
  }

  private replaceRouteParams(pages: any[], params: ParamMap): any[] {
    if (!pages || pages.length === 0) return pages;

    return pages.map((page) => {
      let url = page.url;
      if (url) {
        params.keys.forEach((key) => {
          const value = params.get(key);
          if (value) url = url.replace(`:${key}`, value);
        });
      }
      return { ...page, url };
    });
  }

  private get tenantType(): string {
    return JSON.parse(localStorage.getItem('tenant') ?? '{}').type || '';
  }
}
